/**
 * TransitionBlockAdminPanel — admin panel for workflow transition block rules.
 * Composes RuleListView + RuleConditionBuilder + TransitionBlockActionEditor.
 * Context is always "workflow_transition_block".
 * Spec: Workflow_Transition_Prevention_Rules_Functional_Spec.md, Section 7
 * KAN-199
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { rulesEngineService } from '../../services/api/rulesEngineService';
import { bankService } from '../../services/api/bankService';
import type { Bank } from '../../services/api/bankService';
import { clientService } from '../../services/api/clientService';
import type { TradingEntity } from '../../services/api/clientService';
import { groupingsService } from '../../services/api/groupingsService';
import type { GroupingsConfig } from '../../types/groupings';
import RuleListView from '../rules/RuleListView';
import RuleConditionBuilder from '../rules/RuleConditionBuilder';
import TransitionBlockActionEditor from './TransitionBlockActionEditor';
import AlertModal from '../common/AlertModal';
import type { Rule, RuleCreate, RuleUpdate, Condition, ConditionGroup, ConflictResult, FieldCatalogueEntry } from '../../types/rulesEngine';

const CONTEXT = 'workflow_transition_block';

const SPOT_TYPES = new Set(['Spot']);
const FORWARD_TYPES = new Set(['Forward', 'Forward Asiático', 'Forward Starting']);
const ALL_PRODUCT_TYPES = new Set([...SPOT_TYPES, ...FORWARD_TYPES]);

/** Determine which product types a single ProductType condition matches. */
function matchedProductTypes(cond: Condition): Set<string> {
  switch (cond.operator) {
    case 'equals':
      return new Set([String(cond.value)]);
    case 'not_equals': {
      const s = new Set(ALL_PRODUCT_TYPES);
      s.delete(String(cond.value));
      return s;
    }
    case 'in':
      return new Set((cond.value as string[]).map(String));
    case 'not_in': {
      const s = new Set(ALL_PRODUCT_TYPES);
      (cond.value as string[]).forEach(v => s.delete(String(v)));
      return s;
    }
    default:
      return new Set(ALL_PRODUCT_TYPES);
  }
}

type ViewMode = 'list' | 'create' | 'edit';

const DEFAULT_ACTION: Record<string, unknown> = {
  transitions: [],
};

interface Props {
  productTypeHint: 'spot' | 'forward';
  /** Notify parent when rules are created/edited/copied/deleted so sibling panels reload. */
  onRulesChanged?: () => void;
  /** External refresh key from parent — changes trigger a reload. */
  externalRefreshKey?: number;
}

const TransitionBlockAdminPanel: React.FC<Props> = ({ productTypeHint, onRulesChanged, externalRefreshKey }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Editor form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [conditionGroups, setConditionGroups] = useState<ConditionGroup[]>([]);
  const [action, setAction] = useState<Record<string, unknown>>(DEFAULT_ACTION);

  // Dynamic field catalogue data
  const [banks, setBanks] = useState<Bank[]>([]);
  const [tradingEntities, setTradingEntities] = useState<TradingEntity[]>([]);
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  // UI state
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState<ConflictResult[]>([]);
  const [alertMessage, setAlertMessage] = useState('');

  // Load dynamic data for field catalogue
  useEffect(() => {
    if (!clientId) return;
    const load = async () => {
      const [banksRes, entitiesRes, groupings] = await Promise.all([
        bankService.listBanks().catch(() => null),
        clientService.getTradingEntities(clientId).catch(() => null),
        groupingsService.getGroupings(clientId).catch(() => null),
      ]);
      if (banksRes?.success && banksRes.data) setBanks(banksRes.data);
      if (entitiesRes?.success && entitiesRes.data) setTradingEntities(entitiesRes.data);
      if (groupings) setGroupingsConfig(groupings);
    };
    load();
  }, [clientId]);

  // Build field catalogue — same as WorkflowExceptionsAdminPanel
  const fieldCatalogue = useMemo<FieldCatalogueEntry[]>(() => {
    const NUMBER_OPERATORS: FieldCatalogueEntry['operators'] = [
      'equals', 'not_equals', 'greater_than', 'less_than',
      'greater_than_or_equal', 'less_than_or_equal', 'between',
      'is_empty', 'is_not_empty',
    ];
    const DATE_OPERATORS = NUMBER_OPERATORS;

    const catalogue: FieldCatalogueEntry[] = [
      {
        displayName: t('admin.workflowExceptions.fieldProductType', 'Product Type'),
        field: 'ProductType',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'dropdown',
        options: [
          { label: 'Spot', value: 'Spot' },
          { label: 'Forward', value: 'Forward' },
          { label: 'Forward Asiático', value: 'Forward Asiático' },
          { label: 'Forward Starting', value: 'Forward Starting' },
        ],
      },
      {
        displayName: t('admin.workflowExceptions.fieldSettlementType', 'Settlement Type'),
        field: 'SettlementType',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'dropdown',
        options: [
          { label: 'Entrega Física', value: 'Entrega Física' },
          { label: 'Compensación', value: 'Compensación' },
        ],
      },
      {
        displayName: t('admin.workflowExceptions.fieldDirection', 'Direction'),
        field: 'Direction',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'is_empty', 'is_not_empty'],
        inputType: 'dropdown',
        options: [
          { label: 'Buy', value: 'Buy' },
          { label: 'Sell', value: 'Sell' },
        ],
      },
      {
        displayName: t('admin.workflowExceptions.fieldOurParty', 'Our Party'),
        field: 'OurParty',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'dropdown',
        options: tradingEntities.map(e => ({ label: e.name, value: e.name })),
      },
      {
        displayName: t('admin.workflowExceptions.fieldCounterparty', 'Counterparty'),
        field: 'CounterpartyName',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'dropdown',
        options: banks.filter(b => b.isCounterparty !== false).map(b => ({ label: b.name, value: b.name })),
      },
      {
        displayName: t('admin.workflowExceptions.fieldCurrency1', 'Currency 1'),
        field: 'Currency1',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'text',
      },
      {
        displayName: t('admin.workflowExceptions.fieldCurrency2', 'Currency 2'),
        field: 'Currency2',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'text',
      },
      {
        displayName: t('admin.workflowExceptions.fieldNotionalAmount', 'Notional Amount'),
        field: 'QuantityCurrency1',
        valueType: 'number',
        operators: NUMBER_OPERATORS,
        inputType: 'number',
      },
      {
        displayName: t('admin.workflowExceptions.fieldContraAmount', 'Contra Amount'),
        field: 'QuantityCurrency2',
        valueType: 'number',
        operators: NUMBER_OPERATORS,
        inputType: 'number',
      },
      {
        displayName: t('admin.workflowExceptions.fieldTradeDate', 'Trade Date'),
        field: 'TradeDate',
        valueType: 'date',
        operators: DATE_OPERATORS,
        inputType: 'datepicker',
      },
      {
        displayName: t('admin.workflowExceptions.fieldValueDate', 'Value Date'),
        field: 'ValueDate',
        valueType: 'date',
        operators: DATE_OPERATORS,
        inputType: 'datepicker',
      },
      {
        displayName: t('admin.workflowExceptions.fieldMaturityDate', 'Maturity Date'),
        field: 'MaturityDate',
        valueType: 'date',
        operators: DATE_OPERATORS,
        inputType: 'datepicker',
      },
      {
        displayName: t('admin.workflowExceptions.fieldInternalTrade', 'Internal Trade'),
        field: 'IsInternalTrade',
        valueType: 'string',
        operators: ['equals', 'not_equals'],
        inputType: 'dropdown',
        options: [
          { label: 'true', value: 'true' },
          { label: 'false', value: 'false' },
        ],
      },
      {
        displayName: t('admin.transitionBlock.fieldSpotPrice', 'Spot Price'),
        field: 'SpotPrice',
        valueType: 'number',
        operators: NUMBER_OPERATORS,
        inputType: 'number',
      },
      {
        displayName: t('admin.transitionBlock.fieldForwardPoints', 'Forward Points'),
        field: 'ForwardPoints',
        valueType: 'number',
        operators: NUMBER_OPERATORS,
        inputType: 'number',
      },
      {
        displayName: t('admin.transitionBlock.fieldForwardRate', 'Forward Rate'),
        field: 'ForwardRate',
        valueType: 'number',
        operators: NUMBER_OPERATORS,
        inputType: 'number',
      },
      {
        displayName: t('admin.transitionBlock.fieldHedgeObject', 'Hedge Object'),
        field: 'HedgeObject',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'text',
      },
      {
        displayName: t('admin.transitionBlock.fieldClientReference', 'Client Reference'),
        field: 'ClientReference',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'text',
      },
      {
        displayName: t('admin.transitionBlock.fieldCounterpartyReference', 'Counterparty Reference'),
        field: 'CounterpartyReference',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'text',
      },
    ];

    // Grouping 1 — only if configured
    if (groupingsConfig?.grouping1Label) {
      const activeValues = groupingsConfig.grouping1Values.filter(v => v.isActive);
      catalogue.push({
        displayName: groupingsConfig.grouping1Label,
        field: 'Grouping1',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: activeValues.length > 0 ? 'dropdown' : 'text',
        options: activeValues.map(v => ({ label: v.name, value: v.name })),
      });
    }

    // Grouping 2 — only if label is set
    if (groupingsConfig?.grouping2Label) {
      const activeValues = groupingsConfig.grouping2Values.filter(v => v.isActive);
      catalogue.push({
        displayName: groupingsConfig.grouping2Label,
        field: 'Grouping2',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: activeValues.length > 0 ? 'dropdown' : 'text',
        options: activeValues.map(v => ({ label: v.name, value: v.name })),
      });
    }

    // Grouping 3 — only if label is set
    if (groupingsConfig?.grouping3Label) {
      const activeValues = groupingsConfig.grouping3Values.filter(v => v.isActive);
      catalogue.push({
        displayName: groupingsConfig.grouping3Label,
        field: 'Grouping3',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: activeValues.length > 0 ? 'dropdown' : 'text',
        options: activeValues.map(v => ({ label: v.name, value: v.name })),
      });
    }

    return catalogue;
  }, [banks, tradingEntities, groupingsConfig, t]);

  // ── Product-type filter: only show rules relevant to this column ──

  const ruleFilterFn = useCallback((rule: Rule): boolean => {
    const targetTypes = productTypeHint === 'spot' ? SPOT_TYPES : FORWARD_TYPES;

    // Catch-all (no conditions) → show in both columns
    if (!rule.conditionGroups || rule.conditionGroups.length === 0) return true;

    // Rule matches if ANY condition group (OR) could match a trade of this column's type
    return rule.conditionGroups.some(group => {
      const ptConditions = group.conditions.filter(c => c.field === 'ProductType');
      // No ProductType condition in this group → group matches all product types
      if (ptConditions.length === 0) return true;
      // All ProductType conditions (AND) must allow at least one target type
      return ptConditions.every(cond => {
        const matched = matchedProductTypes(cond);
        return [...targetTypes].some(t => matched.has(t));
      });
    });
  }, [productTypeHint]);

  // ── Table summary functions ──

  const OPERATOR_LABELS: Record<string, string> = {
    equals: '=', not_equals: '\u2260',
    in: '\u2208', not_in: '\u2209',
    greater_than: '>', less_than: '<',
    greater_than_or_equal: '\u2265', less_than_or_equal: '\u2264',
    between: '\u2194', contains: '\u2283', not_contains: '\u2285',
    is_empty: t('rules.operatorIsEmpty', 'is empty'),
    is_not_empty: t('rules.operatorIsNotEmpty', 'is not empty'),
  };

  const conditionSummaryFn = useCallback((rule: Rule): string => {
    if (!rule.conditionGroups || rule.conditionGroups.length === 0) {
      return t('rules.defaultRule', 'Applies to all trades');
    }
    const groupTexts = rule.conditionGroups.map(group => {
      const parts = group.conditions.map(cond => {
        const catalogueEntry = fieldCatalogue.find(f => f.field === cond.field);
        const fieldLabel = catalogueEntry?.displayName || cond.field;
        const op = OPERATOR_LABELS[cond.operator] || cond.operator;

        if (cond.operator === 'is_empty' || cond.operator === 'is_not_empty') {
          return `${fieldLabel} ${op}`;
        }

        let valueStr = '';
        if (Array.isArray(cond.value)) {
          valueStr = cond.value.join(', ');
        } else if (cond.value != null && typeof cond.value === 'object') {
          const obj = cond.value as Record<string, unknown>;
          valueStr = `${obj.from ?? ''}\u2013${obj.to ?? ''}`;
        } else {
          valueStr = String(cond.value ?? '');
        }

        return `${fieldLabel} ${op} ${valueStr}`;
      });
      return parts.join(' & ');
    });
    return groupTexts.join(' | ');
  }, [fieldCatalogue, t]);

  const actionSummaryFn = useCallback((rule: Rule): string => {
    const a = rule.action as Record<string, unknown>;
    const transitions = (a.transitions as string[]) || [];

    if (transitions.length === 0) return '\u2014';

    return transitions.map(key => {
      switch (key) {
        case 'booked_to_confirmed':
          return t('admin.transitionBlock.transitionBookedToConfirmed', 'Booked \u2192 Confirmed');
        case 'confirmed_to_contractSigned':
          return t('admin.transitionBlock.transitionConfirmedToContractSigned', 'Confirmed \u2192 Contract Signed');
        case 'confirmed_to_final':
          return t('admin.transitionBlock.transitionConfirmedToFinal', 'Confirmed \u2192 Final');
        case 'contractSigned_to_final':
          return t('admin.transitionBlock.transitionContractSignedToFinal', 'Contract Signed \u2192 Final');
        default:
          return key;
      }
    }).join(', ');
  }, [t]);

  // Reset form
  const resetForm = () => {
    setName('');
    setDescription('');
    setIsActive(true);
    setConditionGroups([]);
    setAction({ ...DEFAULT_ACTION });
    setConflicts([]);
  };

  // Enter create mode
  const handleCreate = () => {
    resetForm();
    setEditingRule(null);
    setViewMode('create');
  };

  // Enter edit mode
  const handleEdit = (rule: Rule) => {
    setEditingRule(rule);
    setName(rule.name);
    setDescription(rule.description || '');
    setIsActive(rule.isActive);
    setConditionGroups(rule.conditionGroups);
    setAction(rule.action);
    setConflicts([]);
    setViewMode('edit');
  };

  // Copy rule
  const handleCopy = async (rule: Rule) => {
    try {
      const payload: RuleCreate = {
        name: `${rule.name} (${t('rules.copyNoun', 'Copia')})`,
        description: rule.description || undefined,
        context: CONTEXT,
        isActive: rule.isActive,
        conditionGroups: rule.conditionGroups,
        action: rule.action,
      };
      const response = await rulesEngineService.createRule(clientId, payload);
      if (response.success && response.data) {
        setConflicts(response.data.conflicts || []);
        setRefreshKey(k => k + 1);
        onRulesChanged?.();
      }
    } catch (err: any) {
      setAlertMessage(err.message || t('common.error', 'An error occurred'));
    }
  };

  // Back to list
  const handleBack = () => {
    setViewMode('list');
    setEditingRule(null);
    setConflicts([]);
  };

  // Save rule
  const handleSave = async () => {
    if (!name.trim()) {
      setAlertMessage(t('rules.nameRequired', 'Rule name is required.'));
      return;
    }

    const transitions = (action.transitions as string[]) || [];

    if (transitions.length === 0) {
      setAlertMessage(t('admin.transitionBlock.validationNoTransitions', 'At least one transition must be selected.'));
      return;
    }

    setSaving(true);
    try {
      if (viewMode === 'create') {
        const payload: RuleCreate = {
          name: name.trim(),
          description: description.trim() || undefined,
          context: CONTEXT,
          isActive,
          conditionGroups,
          action,
        };
        const response = await rulesEngineService.createRule(clientId, payload);
        if (response.success && response.data) {
          setConflicts(response.data.conflicts || []);
          setRefreshKey(k => k + 1);
          onRulesChanged?.();
          setViewMode('list');
        }
      } else if (viewMode === 'edit' && editingRule) {
        const payload: RuleUpdate = {
          name: name.trim(),
          description: description.trim() || undefined,
          isActive,
          conditionGroups,
          action,
        };
        const response = await rulesEngineService.updateRule(clientId, editingRule.id, payload);
        if (response.success && response.data) {
          setConflicts(response.data.conflicts || []);
          setRefreshKey(k => k + 1);
          onRulesChanged?.();
          setViewMode('list');
        }
      }
    } catch (err: any) {
      setAlertMessage(err.message || t('common.error', 'An error occurred'));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──

  const panelTitle = productTypeHint === 'spot'
    ? t('admin.transitionBlock.titleSpot', 'Transition Block Rules (Spot)')
    : t('admin.transitionBlock.titleForward', 'Transition Block Rules (Forward)');

  if (viewMode === 'list') {
    return (
      <div className="workflow-exceptions-admin-panel transition-block-admin-panel">
        {/* Conflict warning banner */}
        {conflicts.length > 0 && (
          <div className="conflict-warning-banner">
            <div className="conflict-warning-title">
              {t('rules.conflictWarning', 'Warning: Rule overlap detected')}
            </div>
            {conflicts.map((c, i) => (
              <div key={i} className="conflict-item">
                "{c.conflictingRuleName}": {c.overlapDescription}
              </div>
            ))}
            <div className="conflict-dismiss-hint">
              {t('rules.conflictDismissHint', 'This is not an error. Multiple matching rules are allowed. Review your rules to ensure the overlap is intentional.')}
            </div>
            <button className="dismiss-btn" onClick={() => setConflicts([])}>
              {t('common.dismiss', 'Dismiss')}
            </button>
          </div>
        )}

        <RuleListView
          context={CONTEXT}
          title={panelTitle}
          onEdit={handleEdit}
          onCreate={handleCreate}
          onCopy={handleCopy}
          refreshKey={refreshKey + (externalRefreshKey ?? 0)}
          conditionSummaryFn={conditionSummaryFn}
          actionSummaryFn={actionSummaryFn}
          actionSummaryLabel={t('admin.transitionBlock.blocksLabel', 'Blocks')}
          ruleFilterFn={ruleFilterFn}
          onRulesChanged={onRulesChanged}
          allowDeleteCatchAll={true}
        />

        {alertMessage && (
          <AlertModal message={alertMessage} onClose={() => setAlertMessage('')} />
        )}
      </div>
    );
  }

  // Is this a catch-all rule (no conditions)?
  const isDefault = viewMode === 'edit' && editingRule && editingRule.conditionGroups.length === 0;

  // Create / Edit form
  return (
    <div className="workflow-exceptions-admin-panel transition-block-admin-panel">
      <div className="editor-header">
        <h3>
          {viewMode === 'create'
            ? t('admin.transitionBlock.addRule', 'Add Block Rule')
            : t('admin.transitionBlock.editRule', 'Edit Block Rule')}
          {' '}({productTypeHint === 'spot' ? 'Spot' : 'Forward'})
        </h3>
      </div>

      <div className="editor-form">
        {/* Row 1: Name + Description + Active */}
        <div className="form-row-inline">
          <div className="form-field form-field-name">
            <label>{t('rules.name', 'Name')}</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('admin.transitionBlock.namePlaceholder', 'e.g. Forward — require SpotPrice before Confirmed')}
              maxLength={200}
            />
          </div>

          <div className="form-field form-field-desc">
            <label>{t('rules.description', 'Description')} <span className="optional">({t('common.optional', 'optional')})</span></label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('rules.descriptionPlaceholder', 'Brief description')}
              maxLength={500}
            />
          </div>

          {!isDefault && (
            <div className="form-field form-field-active">
              <label className="active-checkbox-label">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                />
                {t('rules.isActive', 'Active')}
              </label>
            </div>
          )}
        </div>

        {/* Row 2: If (Conditions) → Then (Block) */}
        <div className="if-then-layout">
          <div className="if-section">
            <div className="section-label">{t('rules.ifLabel', 'If')}</div>
            <RuleConditionBuilder
              conditionGroups={conditionGroups}
              fieldCatalogue={fieldCatalogue}
              onChange={isDefault ? () => {} : setConditionGroups}
            />
          </div>
          <div className="then-arrow">{'\u2192'}</div>
          <div className="then-section">
            <div className="section-label">{t('admin.transitionBlock.blockLabel', 'Block')}</div>
            <TransitionBlockActionEditor
              action={action}
              onChange={setAction}
              productTypeHint={productTypeHint}
            />
          </div>
        </div>

        <div className="editor-actions">
          <button className="cancel-btn" onClick={handleBack} disabled={saving}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </button>
        </div>
      </div>

      {alertMessage && (
        <AlertModal message={alertMessage} onClose={() => setAlertMessage('')} />
      )}
    </div>
  );
};

export default TransitionBlockAdminPanel;
